import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import wellnessImage from "@/assets/wellness-self-care-menopause.jpg";

export default function BannerGenerator() {
  const [generating, setGenerating] = useState(false);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const generateBanner = async () => {
    setGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-banner');

      if (error) throw error;

      if (data.imageUrl) {
        setBannerUrl(data.imageUrl);
        toast({
          title: "Banner generated!",
          description: "Your X banner is ready to download.",
        });
      }
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate banner. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadBanner = () => {
    if (!bannerUrl) return;

    const link = document.createElement('a');
    link.href = bannerUrl;
    link.download = 'menopause-wellness-banner.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download started",
      description: "Your banner is being downloaded.",
    });
  };

  const downloadWellnessImage = () => {
    const link = document.createElement('a');
    link.href = wellnessImage;
    link.download = 'wellness-self-care-menopause.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download started",
      description: "Your wellness image is being downloaded.",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">← Back Home</Button>
        </Link>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-3xl">
              <Sparkles className="h-8 w-8 text-primary" />
              X Banner Generator
            </CardTitle>
            <CardDescription className="text-lg">
              Generate a beautiful, colorful banner for your X (Twitter) profile showcasing our mission
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="bg-muted/50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3 text-lg">Our Mission (160 characters)</h3>
                <p className="text-muted-foreground leading-relaxed font-medium">
                  Empowering women through menopause with symptom tracking, supportive community, and expert wellness insights. Thrive through every stage.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Perfect for X/Twitter bios and social media profiles
                </p>
              </div>

              <div className="bg-muted/50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3 text-lg">Banner Specifications</h3>
                <ul className="text-muted-foreground space-y-2 text-sm">
                  <li>• Dimensions: 1500x500 pixels (optimized for X/Twitter)</li>
                  <li>• Style: Vibrant, empowering, professional yet warm</li>
                  <li>• Colors: Sunset tones (coral, rose gold, lavender, teal)</li>
                  <li>• Elements: Community, tracking, education icons</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={generateBanner}
                disabled={generating}
                size="lg"
                className="flex-1 gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Generate Banner
                  </>
                )}
              </Button>

              {bannerUrl && (
                <Button
                  onClick={downloadBanner}
                  variant="secondary"
                  size="lg"
                  className="gap-2"
                >
                  <Download className="h-5 w-5" />
                  Download
                </Button>
              )}
            </div>

            {bannerUrl && (
              <div className="space-y-4 animate-fade-in">
                <div className="border-2 border-primary/20 rounded-lg overflow-hidden">
                  <img
                    src={bannerUrl}
                    alt="Generated X Banner"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Right-click the image above to save, or use the Download button
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Sparkles className="h-6 w-6 text-primary" />
              Your Wellness Image
            </CardTitle>
            <CardDescription>
              Photorealistic wellness image for your article content
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-primary/20 rounded-lg overflow-hidden">
              <img
                src={wellnessImage}
                alt="Wellness Self-Care Menopause"
                className="w-full h-auto"
              />
            </div>
            <Button
              onClick={downloadWellnessImage}
              variant="secondary"
              size="lg"
              className="w-full gap-2"
            >
              <Download className="h-5 w-5" />
              Download Wellness Image
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Right-click the image above to save, or use the Download button
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
